import {
  BrandAsset,
  Card,
  Divider,
  List,
  ListItem,
  Text,
  useColors,
  useMediaQuery,
} from "@certn/cascadia-react";
import { useClientPortalAPI } from "@certn/api";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useUser } from "../../hooks/useUser";
import { Order } from "../../pages/order/OrderContext";

interface OrderReviewEmailPreviewProps {
  order: Order;
}

export function OrderReviewEmailPreview(props: OrderReviewEmailPreviewProps) {
  const { mediaQuery } = useMediaQuery();
  const colors = useColors();
  const { t } = useTranslation();
  const { details: userDetails } = useUser();
  const { data: customerGroups = [] } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/customer-groups/`
  );
  const { order } = props;
  const selectedGroup = customerGroups?.find(
    (group) => group.id === order.group
  );
  const companyName =
    selectedGroup?.applicant_facing_name ||
    userDetails?.client_account?.auth0_display_name;
  const bundleChecks = order.bundles.flatMap(({ checks }) => checks);
  const checkNames = order.checks
    .map((check) => check.name)
    .concat(bundleChecks.map((check) => check?.name || ""))
    .concat(order.packageData?.checks?.map((check) => check.name) || []);

  return (
    <div className={styles.emailSampleMainContainer}>
      <Card ariaLabel="order-email-sample" type="large">
        <Text
          as="div"
          className={styles.textPadding}
          style="global/heading-medium"
        >
          {t("Email sample")}
        </Text>
        <div
          className={css([
            {
              backgroundColor: "var(--background-accent-neutral-weak)",
              border: `1px solid ${colors["border-base"]}`,
            },
            {
              paddingBottom: "var(--space-10)",
              paddingTop: "var(--space-6)",
            },
            styles.emailSampleGreyContainerPadding,
            mediaQuery({
              paddingLeft: ["var(--space-3)", "var(--space-10)"],
              paddingRight: ["var(--space-3)", "var(--space-10)"],
            }),
          ])}
        >
          <BrandAsset
            height="32"
            width="110"
            type="full"
            brand="certn"
            className={styles.brandAssetPadding}
          />
          <Card
            className={styles.innerCardSpacing}
            testID="email-sample-inner-content-card"
          >
            <div className={styles.innerCardDisplayStyles}>
              <Text style="global/title-small" as="h2">
                {t(
                  "{{companyName}} has sent you a background check request via Certn",
                  { companyName }
                )}
              </Text>
              <Text as="div">{t("This request includes")}:</Text>
              <List type="unordered">
                {checkNames.map((checkName, index) => (
                  <ListItem
                    key={index}
                    icon="check"
                    iconColor="icon-accent-tofino"
                  >
                    <Text style="form/body-primary">{checkName}</Text>
                  </ListItem>
                ))}
              </List>
              <ul
                aria-hidden="true"
                className={styles.additionalSmallTextContainer}
              >
                <li className={css(styles.emptyListItems, { width: 112 })}>
                  <Text as="div">{"\u00A0"}</Text>
                  <Text>{"\u00A0"}</Text>
                </li>
              </ul>
              {order.message && (
                <div
                  className={css([
                    styles.customMessageStyles,
                    {
                      backgroundColor: "var(--background-selected-weak)",
                    },
                  ])}
                >
                  <Text as="div" style="form/section-heading">
                    {t("Message from {{companyName}}", { companyName })}:
                  </Text>
                  <Text>{order.message}</Text>
                </div>
              )}
            </div>
            <Divider marginVertical="space-7" />
            <ul
              aria-hidden="true"
              className={styles.additionalSmallTextContainer}
            >
              <li className={css(styles.emptyListItems, { width: 80 })}>
                <Text>{"\u00A0"}</Text>
              </li>
              <li className={css(styles.emptyListItems, { width: 160 })}>
                <Text>{"\u00A0"}</Text>
              </li>
              <li className={css(styles.emptyListItems, { width: 160 })}>
                <Text>{"\u00A0"}</Text>
              </li>
            </ul>
            <Divider marginTop="space-7" />
          </Card>
        </div>
      </Card>
    </div>
  );
}

const styles = {
  innerCardSpacing: css({
    paddingTop: "var(--space-9)",
    paddingBottom: "var(--space-9)",
    paddingLeft: "var(--space-8)",
    paddingRight: "var(--space-8)",
  }),
  textPadding: css({
    paddingLeft: "var(--space-4)",
    paddingTop: "var(--space-3)",
    paddingBottom: "var(--space-3)",
  }),
  emailSampleMainContainer: css({ flexBasis: "608px", flexGrow: 1 }),
  emailSampleGreyContainerPadding: css({
    paddingBottom: "var(--space-6)",
    paddingTop: "var(--space-6)",
  }),
  brandAssetPadding: css({
    paddingBottom: "var(--space-3)",
  }),
  innerCardDisplayStyles: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-5)",
  }),
  customMessageStyles: css({
    paddingTop: "var(--space-3)",
    paddingBottom: "var(--space-3)",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
  }),
  additionalSmallTextContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
    listStyleType: "none",
    paddingLeft: 0,
  }),
  emptyListItems: css({
    backgroundColor: "var(--background-accent-neutral-medium)",
    borderRadius: "var(--border-radius-2)",
  }),
};
